import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBrandWhatsapp } from '@tabler/icons-react';

import { AuditPromoSection } from '@components/auditPromoSection';
import { Badge } from '@components/badge';
import { Container } from '@components/container';
import { CTA } from '@components/cta';
import { HeroCarousel } from '@components/heroCarousel/HeroCarousel';
import { IntegrationsFigure } from '@components/integrationsFigure';
import { IntroAIFeaturesSection } from '@components/introAIFeaturesSection/introAIFeaturesSection';
import { IntroCollabFeaturesSection } from '@components/introCollabFeaturesSection/introCollabFeaturesSection';
import { IntroFeaturesSection } from '@components/introFeaturesSection/introFeaturesSection';
import { IntroSection } from '@components/introSection';
import { FlickeringGrid } from '@components/magicui/flickering-grid';
import { WarpBackground } from '@components/magicui/warp-background';
import { FeaturesSectionMain } from '@components/mainFeature';
import { MetricsSection } from '@components/metricsSection';
import { NavbarSection } from '@components/navbar';
import { NotificationsList } from '@components/notificationsList';
import { TitleSection } from '@components/titleSection';
import { Typography } from '@components/typography';

import { isMobileDevice } from '@globalUtils/environment';

// Lazy load heavy below-fold components
const Marquee3D = React.lazy(() => import('@components/marquee3d').then((m) => ({ default: m.Marquee3D })));
const MarqueeView = React.lazy(() => import('@components/marquee').then((m) => ({ default: m.MarqueeView })));
const TextRevealSection = React.lazy(() => import('@components/textReveal'));
const WorldMapView = React.lazy(() =>
  import('@components/worldMap').then((m) => ({ default: m.WorldMapView }))
);
const ComparisonSection = React.lazy(() =>
  import('@components/comparison').then((m) => ({ default: m.ComparisonSection }))
);

// Lightweight loading placeholder
const SectionLoader = () => (
  <div className="w-full h-[200px] flex items-center justify-center">
    <div className="animate-pulse bg-gray-100 rounded-lg w-full h-full" />
  </div>
);

const HomePage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="w-full h-fit" style={{ overscrollBehaviorY: 'none' }}>
      <NavbarSection />

      <div className="w-full h-fit flex flex-col items-center" style={{ minHeight: '100vh' }}>
        {/* Fixed warp background that covers only first viewport */}
        <div className="w-full h-[65vh] lg:h-[70vh]">
          <WarpBackground gridColor="#f0f0f0" className="w-full h-full">
            <div className="w-full h-full flex items-center justify-center pointer-events-auto">
              {/*
              <div
                id="dotBG2"
                className={cn(
                  'absolute inset-0 z-0',
                  '[background-size:20px_20px]',
                  '[background-image:radial-gradient(#d4d4d4_1px,transparent_1px)]',
                  'dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]'
                )}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] dark:bg-black"></div>
              */}

              <div className="absolute size-[600px] w-full overflow-hidden rounded-lg bg-background opacity-[.6]">
                <FlickeringGrid
                  className=" inset-0 z-0 [mask-image:radial-gradient(300px_circle_at_center,white,transparent)] lg:[mask-image:radial-gradient(450px_circle_at_center,white,transparent)]"
                  squareSize={4}
                  gridGap={6}
                  color="#60A5FA"
                  maxOpacity={0.5}
                  flickerChance={0.1}
                  height={800}
                  width={800}
                />
              </div>
              {/*
                 <div className="w-full h-full absolute flex justify-center items-center">
                <div className="rounded-full! mx-auto my-auto w-[250px] h-[250px] lg:w-[350px] lg:h-[350px] pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,white_0%,black)] dark:bg-black"></div>
              </div>
                */}

              <div className="w-full h-full flex items-center justify-center">
                <Container className="w-full! mt-[10vh]">
                  <TitleSection>
                    <Badge text={t('Launching Soon')} />

                    <Typography align="center" variant="titlesmall" highlightedLines={[1]}>
                      {t('main-title')}
                    </Typography>

                    <div className={`flex flex-col ${isMobileDevice() ? 'px-0' : ''}`}>
                      <Typography
                        align={isMobileDevice() ? 'start' : 'center'}
                        variant="subtitle"
                        className={`font-medium ${isMobileDevice() ? 'text-base!' : ''}`}
                      >
                        {t('main-subtitle')}
                      </Typography>

                      <Typography
                        align={isMobileDevice() ? 'start' : 'center'}
                        variant="small"
                        className={`text-gray-500 mt-2 ${isMobileDevice() ? 'text-sm!' : 'px-16!'}`}
                      >
                        {t('main-second-subtitle')}
                      </Typography>
                    </div>

                    <div className="z-50 flex flex-col">
                      <div className="flex gap-4 my-3">
                        <a
                          href="https://w.app/rzv1fu"
                          className="no-underline flex flex-col items-center gap-2"
                          target="_blank"
                          rel="noreferrer"
                          data-id="boton-3"
                        >
                          <div className="w-[240px] flex items-center justify-center gap-2 font-bold cursor-pointer py-2 bg-black border-1 border-black rounded-xl text-white shadow-lg">
                            <IconBrandWhatsapp size={20} />
                            {t('Book a demo')}
                          </div>
                          <Typography variant="caption" className="text-gray-400">
                            {t('Try for free')}
                          </Typography>
                        </a>

                        {/* <a
                          href="https://console.usecloser.ai/"
                          className="no-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div className="w-[170px] flex justify-center font-bold cursor-pointer py-2 bg-black border-1 border-black rounded-xl text-white shadow-lg">
                            {t('Try for free')}
                          </div>
                        </a> */}
                      </div>
                      {/* 
                      <Typography
                        align="center"
                        variant="caption"
                        className="mt-2 font-semibold text-gray-400!"
                      >
                        {t('No credit card required')}
                      </Typography> */}
                    </div>
                  </TitleSection>
                </Container>
              </div>
            </div>
          </WarpBackground>
        </div>

        <HeroCarousel />

        <div className="rounded-4xl w-full flex-col items-center mb-[7vh] lg:mt-[10vh] lg:mb-[7vh] pt-[7vh] lg:pt-[15vh] px-10 bg-[#fafafa]">
          <Typography variant="titlesmall" align="center">
            {t('lovedByThousands')}
          </Typography>

          <MetricsSection />

          <div className="w-full mb-[3vh] lg:mb-[7vh] mt-[7vh] lg:mt-[15vh]">
            <Suspense fallback={<SectionLoader />}>
              <Marquee3D />
            </Suspense>
          </div>
        </div>

        <Container className="lg:w-[80vw] mt-[0vh] lg:mt-[15vh]">
          <IntroFeaturesSection />
        </Container>

        <Container className="lg:w-full mt-[7vh] lg:mt-[15vh] bg-[#fafafa] rounded-4xl">
          <div className="lg:px-[10vw] py-[5.5vh] pb-[7vh] lg:py-[15vh] lg:pb-[15vh]">
            <IntroCollabFeaturesSection />
          </div>
        </Container>

        <Container className="lg:w-[80vw] mt-[5vh] lg:mt-[15vh]">
          <IntroAIFeaturesSection />
        </Container>

        <Container className="lg:w-[80vw]">
          <FeaturesSectionMain />
        </Container>

        {/* All other sections with white background */}
        <div
          className="w-full bg-white relative z-10 flex flex-col items-center"
          style={{ position: 'relative', transform: 'translateZ(0)' }}
        >
          <div className="w-full h-[1px] border-b-1 border-dashed mt-[7vh] lg:mt-[28vh]"></div>
          <Container className="lg:w-[80vw] mt-[7vh] lg:mt-[15vh]">
            <IntroSection />
          </Container>

          {/* Audit Promo Section */}
          <AuditPromoSection />

          <Container className="lg:w-[80vw] mt-[7vh] lg:mt-[15vh] flex-row items-center lg:gap-0 flex-wrap lg:flex-nowrap">
            <div className="flex flex-col flex-wrap lg:flex-nowrap gap-8">
              <Typography variant="titlesmall">{t('notificationTitle')}</Typography>
              <Typography className="w-full lg:w-[90%]">{t('notificationDescription')}</Typography>
              {!isMobileDevice() && <CTA data-id="boton-12" />}
            </div>
            <NotificationsList />

            {isMobileDevice() && (
              <div className="w-full flex justify-center">
                <CTA data-id="boton-13" />
              </div>
            )}
          </Container>

          <div className="w-full h-fit mt-[7vh] lg:mt-[20vh]">
            <Typography variant="titlesmall">{t('thousands-features')}</Typography>
            <Typography variant="base" align="center" className="mb-[3vh] mt-3">
              {t('thousands-features-description')}
            </Typography>
            <Suspense fallback={<SectionLoader />}>
              <MarqueeView />
            </Suspense>
          </div>

          <Container className="lg:w-[80vw] mt-[0vh] lg:mt-[15vh]">
            <div className="w-full h-full flex flex-wrap mt-10 lg:mt-0 lg:flex-nowrap justify-center items-center gap-8 lg:gap-15">
              <IntegrationsFigure className="order-2 lg:order-1 shrink-0" />
              <div className="w-full lg:w-[460px] order-1 lg:order-2 flex flex-col flex-wrap lg:flex-nowrap gap-4">
                <Typography variant="header" className="lg:text-start items-start">
                  {t('integrated')}
                </Typography>
                <Typography className="w-full lg:text-start items-start">
                  {t('integratedDescription')}
                </Typography>
                {!isMobileDevice() && (
                  <div className="w-full flex justify-start">
                    <CTA data-id="boton-14" />
                  </div>
                )}
              </div>
            </div>
            {isMobileDevice() && (
              <div className="w-full flex justify-center mt-[5vh]">
                <CTA data-id="boton-15" />
              </div>
            )}
          </Container>

          <Container className="w-full px-0 lg:w-[80vw] mt-[7vh] lg:mt-[15vh]">
            <Suspense fallback={<SectionLoader />}>
              <ComparisonSection />
            </Suspense>
          </Container>

          <Container className="lg:w-[900px]">
            <Suspense fallback={<SectionLoader />}>
              <TextRevealSection
                text={`${t('testimonial1')}\n${t('testimonial1-who')}\n${t('testimonial1-where')}`}
              />
            </Suspense>
          </Container>

          <div
            className="w-full"
            style={{
              contain: 'size layout',
              height: 'fit-content',
              overflowAnchor: 'none',
            }}
          >
            <div
              className="w-full"
              style={{
                position: 'static',
                overflowAnchor: 'none',
                contain: 'layout',
                height: 'fit-content',
              }}
            >
              <Suspense fallback={<SectionLoader />}>
                <WorldMapView />
              </Suspense>
            </div>
          </div>
          <div className="h-[70vh]"></div>

          <div className="z-30 w-full p-4 flex justify-center items-center bg-zinc-100">
            <Typography variant="caption" className="text-zinc-800">
              {t('copyright')}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
